import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';

// Componente para dibujar firma
const FirmaCanvas = ({ preguntaId, respuestas, setRespuestas }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasFirma, setHasFirma] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#000';
    }
  }, []);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasFirma(true);
  };

  const stopDrawing = () => {
    if (isDrawing && hasFirma) {
      const canvas = canvasRef.current;
      const firmaBase64 = canvas.toDataURL();
      setRespuestas(prev => ({
        ...prev,
        [preguntaId]: firmaBase64
      }));
    }
    setIsDrawing(false);
  };

  const limpiarFirma = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasFirma(false);
    setRespuestas(prev => {
      const newRespuestas = { ...prev };
      delete newRespuestas[preguntaId];
      return newRespuestas;
    });
  };

  return (
    <div className="space-y-3">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          className="w-full bg-white rounded cursor-crosshair border border-gray-200"
          style={{ touchAction: 'none' }}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <i className="fas fa-pen text-indigo-600"></i>
          <span>Dibuja tu firma con el mouse</span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={limpiarFirma}
          className="text-red-600 hover:text-red-700"
        >
          <i className="fas fa-redo mr-2"></i>
          Limpiar
        </Button>
      </div>
      {hasFirma && (
        <div className="flex items-center space-x-2 text-sm text-green-600 bg-green-50 p-2 rounded">
          <i className="fas fa-check-circle"></i>
          <span>Firma guardada correctamente</span>
        </div>
      )}
    </div>
  );
};

const ResponderFormulario = () => {
  const { formularioId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [formulario, setFormulario] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [estudiantes, setEstudiantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [yaRespondido, setYaRespondido] = useState(false);
  const [respuestaExistente, setRespuestaExistente] = useState(null);
  const [formularioVencido, setFormularioVencido] = useState(false);

  useEffect(() => {
    loadFormulario();
    loadEstudiantes();
  }, [formularioId]);

  // Auto-marcar respuestas para tipos embebidos (YouTube/Wix)
  useEffect(() => {
    if (preguntas.length > 0) {
      preguntas.forEach(pregunta => {
        // Para YouTube embebido, marcar automáticamente como visualizado
        if (pregunta.tipo === 'link_youtube' && pregunta.video_url && !respuestas[pregunta.id]) {
          handleRespuestaChange(pregunta.id, 'visualizado');
        }
        // Para Wix embebido, marcar automáticamente como completado  
        if (pregunta.tipo === 'link_wix' && pregunta.wix_url && !respuestas[pregunta.id]) {
          handleRespuestaChange(pregunta.id, 'completado');
        }
        // Para formulario interno embebido, marcar automáticamente como completado
        if (pregunta.tipo === 'link_formulario_interno' && pregunta.formulario_publico_url && !respuestas[pregunta.id]) {
          handleRespuestaChange(pregunta.id, 'completado');
        }
      });
    }
  }, [preguntas, respuestas]);

  const loadFormulario = async () => {
    try {
      const [formularioRes, preguntasRes] = await Promise.all([
        axios.get(`${API}/formularios/${formularioId}`),
        axios.get(`${API}/formularios/${formularioId}/preguntas`)
      ]);
      
      setFormulario(formularioRes.data);
      setPreguntas(preguntasRes.data);
      
      // Check if form is past deadline
      const formulario = formularioRes.data;
      const now = new Date();
      const fechaLimite = formulario.fecha_limite ? new Date(formulario.fecha_limite) : null;
      
      if (fechaLimite && now > fechaLimite) {
        setFormularioVencido(true);
        toast.error('Este formulario ya no está disponible. La fecha límite ha vencido.');
        return;
      }
      
      // Check if student already has a response
      try {
        const respuestaRes = await axios.get(`${API}/formularios/${formularioId}/mi-respuesta`);
        const miRespuesta = respuestaRes.data;
        
        setYaRespondido(true);
        setRespuestaExistente(miRespuesta);
        
        // If already graded, show results message (no automatic redirect)
        if (miRespuesta.calificado) {
          toast.success('Tu respuesta ya ha sido calificada. Puedes ver tus resultados.', {
            duration: 4000
          });
        }
        
        // If not graded yet, show message but don't allow re-submission
        toast.warning('Ya has respondido este formulario. Espera a que el profesor lo califique.');
        
      } catch (error) {
        // If no response found (404), continue normally
        if (error.response?.status === 404) {
          // Initialize respuestas object for new response
          const initialRespuestas = {};
          preguntasRes.data.forEach(pregunta => {
            initialRespuestas[pregunta.id] = '';
          });
          
          // AUTO-RESTORE: Check localStorage for saved answers
          const savedKey = `formulario_respuestas_${formularioId}`;
          const saved = localStorage.getItem(savedKey);
          if (saved) {
            try {
              const savedRespuestas = JSON.parse(saved);
              let restored = 0;
              Object.entries(savedRespuestas).forEach(([key, val]) => {
                if (val && initialRespuestas.hasOwnProperty(key)) {
                  initialRespuestas[key] = val;
                  restored++;
                }
              });
              if (restored > 0) {
                toast.info(`Se recuperaron ${restored} respuesta(s) guardada(s) anteriormente`, { duration: 4000 });
              }
            } catch (e) {}
          }
          
          setRespuestas(initialRespuestas);
        } else {
          console.error('Error checking existing response:', error);
        }
      }
      
    } catch (error) {
      console.error('Error loading formulario:', error);
      toast.error('Error al cargar formulario');
      navigate('/app/clases');
    } finally {
      setLoading(false);
    }
  };

  // AUTO-SAVE: Save answers to localStorage as student types
  const STORAGE_KEY = `formulario_respuestas_${formularioId}`;

  // Save to localStorage whenever answers change
  useEffect(() => {
    if (Object.keys(respuestas).length > 0 && !yaRespondido) {
      const toSave = {};
      Object.entries(respuestas).forEach(([key, val]) => {
        if (val && val !== '' && val !== 'visualizado' && val !== 'completado') {
          toSave[key] = val;
        }
      });
      if (Object.keys(toSave).length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      }
    }
  }, [respuestas, yaRespondido, STORAGE_KEY]);

  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas({
      ...respuestas,
      [preguntaId]: valor
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare responses for submission
    const respuestasArray = preguntas.map(pregunta => {
      const respuestaBase = {
        pregunta_id: pregunta.id,
        respuesta: respuestas[pregunta.id] || ''
      };
      
      // Para preguntas tipo firma, agregar campo especial
      if (pregunta.tipo === 'firma' && respuestas[pregunta.id]) {
        respuestaBase.firma_base64 = respuestas[pregunta.id];
      }
      
      return respuestaBase;
    });

    // VALIDACIÓN DESACTIVADA: Permitir enviar formulario con preguntas en blanco
    // const emptyResponses = respuestasArray.filter(r => !r.respuesta.trim());
    // if (emptyResponses.length > 0) {
    //   toast.error('Por favor responde todas las preguntas');
    //   return;
    // }

    setSubmitting(true);
    try {
      await axios.post(`${API}/formularios/${formularioId}/responder`, {
        respuestas: respuestasArray
      });
      
      // Clear auto-saved answers after successful submission
      localStorage.removeItem(STORAGE_KEY);
      
      toast.success('Formulario enviado exitosamente');
      
      // Si es el formulario de datos del estudiante, siempre ir al proceso de registro
      const FORMULARIO_REGISTRO_ID = "de6750e1-0c28-4c20-9561-9c1492860c41";
      if (formularioId === FORMULARIO_REGISTRO_ID && ['estudiante', 'pastor', 'graduado'].includes(user?.rol)) {
        localStorage.removeItem('volverAProcesoRegistro');
        toast.success('¡Datos guardados! Ahora puedes continuar con tu inscripción.', { duration: 4000 });
        navigate('/app/proceso-registro');
        return;
      }
      
      // Verificar si debe volver al proceso de registro (otros formularios)
      const volverAProcesoRegistro = localStorage.getItem('volverAProcesoRegistro');
      if (volverAProcesoRegistro) {
        localStorage.removeItem('volverAProcesoRegistro');
        toast.success('¡Datos guardados! Ahora puedes continuar con tu inscripción.', { duration: 4000 });
        navigate('/app/proceso-registro');
        return;
      }
      
      // Navegación condicional: independientes van a gestion-archivos, clase-relacionados van a clases
      if (formulario?.tipo === 'independiente' || !formulario?.clase_id) {
        navigate('/app/formularios-independientes');
      } else {
        navigate('/app/clases');
      }
    } catch (error) {
      console.error('Error submitting formulario:', error);
      
      // Mostrar mensaje de error específico del backend
      let errorMessage = 'Error al enviar formulario';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 403) {
        errorMessage = 'No tienes permisos para responder este formulario';
      } else if (error.response?.status === 400) {
        errorMessage = 'Ya has respondido este formulario anteriormente';
      } else if (error.response?.status === 410) {
        errorMessage = 'La fecha límite para este formulario ha vencido';
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const loadEstudiantes = async () => {
    try {
      const response = await axios.get(`${API}/users/estudiantes`);
      setEstudiantes(response.data || []);
      console.log('Estudiantes cargados correctamente:', response.data?.length || 0);
    } catch (error) {
      console.error('Error cargando estudiantes:', error);
      if (error.response?.status === 403) {
        console.warn('No tienes permisos para ver la lista de estudiantes');
      }
      // No es crítico si falla, solo no mostrará el selector
      setEstudiantes([]);
    }
  };

  const getTipoInput = (pregunta) => {
    // Párrafo de lectura no necesita input
    if (pregunta.tipo === 'parrafo_lectura') {
      return null;
    }
    
    switch (pregunta.tipo) {
      case 'opcion_multiple':
        return (
          <div className="space-y-2">
            {pregunta.opciones?.map((opcion, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={`pregunta_${pregunta.id}`}
                  id={`pregunta_${pregunta.id}_opcion_${index}`}
                  value={opcion}
                  checked={respuestas[pregunta.id] === opcion}
                  onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor={`pregunta_${pregunta.id}_opcion_${index}`} className="text-sm">
                  {opcion}
                </Label>
              </div>
            ))}
          </div>
        );
        
      case 'verdadero_falso':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                name={`pregunta_${pregunta.id}`}
                id={`pregunta_${pregunta.id}_verdadero`}
                value="Verdadero"
                checked={respuestas[pregunta.id] === 'Verdadero'}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <Label htmlFor={`pregunta_${pregunta.id}_verdadero`} className="text-sm text-green-700">
                Verdadero
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                name={`pregunta_${pregunta.id}`}
                id={`pregunta_${pregunta.id}_falso`}
                value="Falso"
                checked={respuestas[pregunta.id] === 'Falso'}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <Label htmlFor={`pregunta_${pregunta.id}_falso`} className="text-sm text-red-700">
                Falso
              </Label>
            </div>
          </div>
        );
        
      case 'respuesta_corta':
        return (
          <Input
            type="text"
            placeholder="Escribe tu respuesta aquí..."
            value={respuestas[pregunta.id] || ''}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            className="w-full"
          />
        );

      case 'respuesta_larga':
        return (
          <div className="space-y-2">
            <Textarea
              placeholder="Escribe tu respuesta detallada aquí... Puedes usar hasta 2000 caracteres."
              value={respuestas[pregunta.id] || ''}
              onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
              rows={6}
              maxLength={2000}
              className="w-full resize-none"
            />
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>📝 Respuesta larga - Sé detallado y completo</span>
              <span>
                {(respuestas[pregunta.id] || '').length}/2000 caracteres
              </span>
            </div>
          </div>
        );
        
      case 'ensayo':
        return (
          <Textarea
            placeholder="Desarrolla tu respuesta aquí..."
            value={respuestas[pregunta.id] || ''}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            rows={4}
            className="w-full"
          />
        );
        
      case 'fecha':
        return (
          <Input
            type="date"
            value={respuestas[pregunta.id] || ''}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            className="w-full"
          />
        );

      case 'seleccion_estudiante':
        // Mostrar selector de estudiantes
        if (estudiantes.length === 0) {
          return (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-2 text-yellow-700">
                <i className="fas fa-exclamation-triangle"></i>
                <span>No se encontraron estudiantes disponibles. Si eres profesor, contacta al administrador.</span>
              </div>
            </div>
          );
        }
        
        return (
          <Select
            value={respuestas[pregunta.id] || ''}
            onValueChange={(value) => handleRespuestaChange(pregunta.id, value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Seleccionar estudiante" />
            </SelectTrigger>
            <SelectContent>
              {estudiantes.map((estudiante) => (
                <SelectItem key={estudiante.id} value={estudiante.nombre_completo}>
                  {estudiante.nombre_completo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'link_wix':
        // Si la pregunta tiene un wix_url configurado, mostrar el formulario embebido
        if (pregunta.wix_url) {
          return (
            <div className="space-y-4">
              <div className="w-full bg-yellow-50 rounded-lg overflow-hidden shadow-lg border border-yellow-200">
                <iframe
                  src={pregunta.wix_url}
                  title="Formulario de Wix"
                  frameBorder="0"
                  width="100%"
                  height="600"
                  className="w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                ></iframe>
              </div>
              <div className="flex items-center space-x-2 text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <i className="fas fa-external-link-alt text-yellow-600"></i>
                <span>Completa el formulario anterior y luego continúa con las siguientes preguntas</span>
              </div>
            </div>
          );
        } else {
          // Fallback: si no hay wix_url configurado, mostrar campo de entrada
          return (
            <div className="space-y-3">
              <Input
                type="url"
                placeholder="Pega aquí el link de tu formulario Wix completado..."
                value={respuestas[pregunta.id] || ''}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                className="w-full"
              />
              <div className="flex items-center space-x-2 text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <i className="fas fa-external-link-alt text-yellow-600"></i>
                <span>Comparte el enlace de tu formulario Wix completado</span>
              </div>
            </div>
          );
        }

      case 'link_formulario_interno':
        // Si la pregunta tiene un formulario_publico_url configurado, mostrar el formulario embebido
        if (pregunta.formulario_publico_url) {
          return (
            <div className="space-y-4">
              <div className="w-full bg-blue-50 rounded-lg overflow-hidden shadow-lg border border-blue-200">
                <iframe
                  src={pregunta.formulario_publico_url}
                  title="Formulario de IBA"
                  frameBorder="0"
                  width="100%"
                  height="700"
                  className="w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                ></iframe>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <i className="fas fa-file-alt text-blue-600"></i>
                <span>Completa el formulario anterior y luego continúa con las siguientes preguntas</span>
              </div>
            </div>
          );
        } else {
          // Fallback: si no hay formulario_publico_url configurado, mostrar campo de entrada
          return (
            <div className="space-y-3">
              <Input
                type="url"
                placeholder="Pega aquí el link del formulario de IBA..."
                value={respuestas[pregunta.id] || ''}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                className="w-full"
              />
              <div className="flex items-center space-x-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <i className="fas fa-file-alt text-blue-600"></i>
                <span>Comparte el enlace del formulario de IBA</span>
              </div>
            </div>
          );
        }

      case 'link_youtube':
        // Si la pregunta tiene un video_url configurado, mostrar el video embebido
        if (pregunta.video_url) {
          // Convertir URL de YouTube o Vimeo a formato embed
          const getEmbedUrl = (url) => {
            // YouTube
            if (url.includes('youtube.com/watch?v=')) {
              const videoId = url.split('v=')[1].split('&')[0];
              return `https://www.youtube.com/embed/${videoId}`;
            } else if (url.includes('youtu.be/')) {
              const videoId = url.split('youtu.be/')[1].split('?')[0];
              return `https://www.youtube.com/embed/${videoId}`;
            } else if (url.includes('youtube.com/embed/')) {
              return url;
            }
            // Vimeo
            else if (url.includes('vimeo.com/')) {
              const vimeoId = url.split('vimeo.com/')[1].split('?')[0].split('/')[0];
              return `https://player.vimeo.com/video/${vimeoId}`;
            } else if (url.includes('player.vimeo.com/')) {
              return url;
            }
            return null;
          };

          const embedUrl = getEmbedUrl(pregunta.video_url);
          const isVimeo = pregunta.video_url.includes('vimeo.com');

          return (
            <div className="space-y-4">
              {embedUrl ? (
                <div className="aspect-video w-full bg-gray-100 rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    src={embedUrl}
                    title={isVimeo ? "Video de Vimeo" : "Video de YouTube"}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              ) : (
                <div className="aspect-video w-full bg-gray-50 rounded-lg flex items-center justify-center border-2 border-gray-200">
                  <div className="text-center text-gray-600">
                    <i className="fas fa-video text-4xl mb-2"></i>
                    <p className="text-sm">Video no disponible</p>
                  </div>
                </div>
              )}
              <div className={`flex items-center space-x-2 text-sm p-3 rounded-lg border ${
                isVimeo 
                  ? 'text-blue-700 bg-blue-50 border-blue-200' 
                  : 'text-red-700 bg-red-50 border-red-200'
              }`}>
                <i className={isVimeo ? "fab fa-vimeo-v text-blue-600" : "fab fa-youtube text-red-600"}></i>
                <span>Ve el video completo y luego continúa con las siguientes preguntas</span>
              </div>
            </div>
          );
        } else {
          // Fallback: si no hay video_url configurado, mostrar campo de entrada
          return (
            <div className="space-y-3">
              <Input
                type="url"
                placeholder="Pega aquí el link de YouTube del video solicitado..."
                value={respuestas[pregunta.id] || ''}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                className="w-full"
              />
              <div className="flex items-center space-x-2 text-sm text-red-700 bg-red-50 p-3 rounded-lg border border-red-200">
                <i className="fab fa-youtube text-red-600"></i>
                <span>Comparte el enlace de YouTube del video solicitado</span>
              </div>
            </div>
          );
        }

      case 'escala_valoracion':
        return (
          <div className="space-y-3">
            {pregunta.opciones?.map((opcion, index) => {
              const puntaje = pregunta.opciones_puntajes?.[opcion] || 0;
              return (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 border-2 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
                  onClick={() => handleRespuestaChange(pregunta.id, opcion)}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name={`pregunta_${pregunta.id}`}
                      id={`pregunta_${pregunta.id}_opcion_${index}`}
                      value={opcion}
                      checked={respuestas[pregunta.id] === opcion}
                      onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <Label 
                      htmlFor={`pregunta_${pregunta.id}_opcion_${index}`} 
                      className="text-base font-medium cursor-pointer"
                    >
                      {opcion}
                    </Label>
                  </div>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800 font-bold">
                    {puntaje} pts
                  </Badge>
                </div>
              );
            })}
          </div>
        );

      case 'firma':
        return <FirmaCanvas preguntaId={pregunta.id} respuestas={respuestas} setRespuestas={setRespuestas} />;

      case 'subir_foto':
        return (
          <div className="space-y-3">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              {respuestas[pregunta.id] ? (
                <div className="space-y-3">
                  <div className="relative inline-block">
                    <img 
                      src={respuestas[pregunta.id]} 
                      alt="Foto subida" 
                      className="max-w-full max-h-64 rounded-lg shadow-md mx-auto"
                    />
                    <button
                      type="button"
                      onClick={() => handleRespuestaChange(pregunta.id, '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 shadow-md"
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                  <p className="text-sm text-green-600 font-medium">
                    <i className="fas fa-check-circle mr-1"></i>
                    Foto cargada correctamente
                  </p>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <div className="space-y-2">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                      <i className="fas fa-camera text-3xl text-blue-500"></i>
                    </div>
                    <p className="text-gray-700 font-medium">Haz clic para subir una foto</p>
                    <p className="text-sm text-gray-500">PNG, JPG o JPEG (máx. 5MB)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('La imagen no debe superar los 5MB');
                          return;
                        }
                        
                        // Convertir a base64
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          handleRespuestaChange(pregunta.id, event.target.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
              )}
            </div>
            <div className="flex items-center space-x-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-lg border border-blue-200">
              <i className="fas fa-camera text-blue-600"></i>
              <span>Sube una foto clara y legible relacionada con la pregunta</span>
            </div>
          </div>
        );
        
      default:
        return (
          <Input
            type="text"
            placeholder="Escribe tu respuesta aquí..."
            value={respuestas[pregunta.id] || ''}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            className="w-full"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  if (!formulario) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Formulario no encontrado</p>
        <Button 
          onClick={() => navigate('/app/clases')} 
          className="mt-4"
        >
          Volver a Clases
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <Button
          variant="ghost"
          onClick={() => {
            // Navegación condicional basada en tipo de formulario
            if (formulario?.tipo === 'independiente' || !formulario?.clase_id) {
              navigate('/app/formularios-independientes');
            } else {
              navigate('/app/clases');
            }
          }}
          className="mb-4 text-blue-600 hover:text-blue-800"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {formulario.titulo}
            </h1>
            <p className="text-gray-600 mb-4">
              {formulario.descripcion}
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <i className="fas fa-user mr-1"></i>
                Profesor: {formulario.profesor_nombre}
              </div>
              <div className="flex items-center">
                <i className="fas fa-star mr-1"></i>
                {formulario.puntuacion_total} puntos
              </div>
              {formulario.fecha_limite && (
                <div className="flex items-center text-orange-600">
                  <i className="fas fa-clock mr-1"></i>
                  Fecha límite: {new Date(formulario.fecha_limite).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
          <Badge 
            variant="outline" 
            className={yaRespondido ? (respuestaExistente?.calificado ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700") : "bg-blue-50 text-blue-700"}
          >
            {yaRespondido ? (
              respuestaExistente?.calificado ? (
                <>
                  <i className="fas fa-star mr-1"></i>
                  Calificado
                </>
              ) : (
                <>
                  <i className="fas fa-clock mr-1"></i>
                  Pendiente
                </>
              )
            ) : (
              <>
                <i className="fas fa-clipboard-list mr-1"></i>
                Responder Formulario
              </>
            )}
          </Badge>
        </div>
      </div>

      {/* Conditional Content */}
      {formularioVencido ? (
        /* Form Expired - Show Error Message */
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <i className="fas fa-clock text-red-600 text-6xl"></i>
              </div>
              <h2 className="text-2xl font-semibold text-red-800 mb-2">
                Formulario Vencido
              </h2>
              <p className="text-red-700 mb-4">
                La fecha límite para responder este formulario ha vencido.
              </p>
              <div className="bg-white p-4 rounded-lg mb-4">
                <div className="text-sm text-gray-600">Fecha límite:</div>
                <div className="font-semibold text-red-600">
                  <i className="fas fa-calendar-times mr-1"></i>
                  {formulario?.fecha_limite ? new Date(formulario.fecha_limite).toLocaleDateString('es-UY', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'No especificada'}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  if (formulario?.tipo === 'independiente' || !formulario?.clase_id) {
                    navigate('/app/formularios-independientes');
                  } else {
                    navigate('/app/clases');
                  }
                }}
                className="mt-4"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Volver
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : yaRespondido && respuestaExistente ? (
        /* Already Responded - Show Status */
        <div className="space-y-6">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex items-center justify-center mb-4">
                  <i className="fas fa-check-circle text-yellow-600 text-6xl"></i>
                </div>
                <h2 className="text-2xl font-semibold text-yellow-800 mb-2">
                  Ya has respondido este formulario
                </h2>
                {respuestaExistente.calificado ? (
                  <>
                    <p className="text-yellow-700 mb-4">
                      Tu respuesta ha sido calificada por el profesor.
                    </p>
                    <div className="space-y-3">
                      <Button
                        onClick={() => navigate(`/app/formulario/${formularioId}/resultados`)}
                        className="instituto-gradient text-white"
                      >
                        <i className="fas fa-star mr-2"></i>
                        Ver Resultados
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (formulario?.tipo === 'independiente' || !formulario?.clase_id) {
                            navigate('/app/formularios-independientes');
                          } else {
                            navigate('/app/clases');
                          }
                        }}
                      >
                        Volver
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-yellow-700 mb-4">
                      El profesor aún no ha calificado tu respuesta. Te notificaremos cuando esté lista.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-sm text-gray-600">Fecha de envío</div>
                        <div className="font-semibold text-gray-900">
                          {new Date(respuestaExistente.fecha_envio).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-sm text-gray-600">Estado</div>
                        <div className="font-semibold text-yellow-600">
                          <i className="fas fa-clock mr-1"></i>
                          Pendiente de calificación
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-sm text-gray-600">Preguntas respondidas</div>
                        <div className="font-semibold text-green-600">
                          {respuestaExistente.respuestas?.length || 0} / {preguntas.length}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (formulario?.tipo === 'independiente' || !formulario?.clase_id) {
                          navigate('/app/formularios-independientes');
                        } else {
                          navigate('/app/clases');
                        }
                      }}
                      className="mt-4"
                    >
                      Volver
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Can Respond - Show Form */
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Barra de progreso */}
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progreso del Formulario
              </span>
              <span className="text-sm text-gray-600">
                {preguntas.filter(p => p.tipo !== 'parrafo_lectura').length} preguntas
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${(Object.keys(respuestas).length / preguntas.filter(p => p.tipo !== 'parrafo_lectura').length) * 100}%` 
                }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {Object.keys(respuestas).length} de {preguntas.filter(p => p.tipo !== 'parrafo_lectura').length} respondidas
            </p>
          </div>

          {preguntas.map((pregunta, index) => {
            // Iconos por tipo de pregunta
            const getIconByType = (tipo) => {
              const icons = {
                'parrafo_lectura': '📖',
                'opcion_multiple': '☑️',
                'respuesta_corta': '📝',
                'respuesta_larga': '📄',
                'ensayo': '✍️',
                'verdadero_falso': '❓',
                'fecha': '📅',
                'seleccion_estudiante': '👥',
                'escala_valoracion': '⭐',
                'firma': '✒️',
                'subir_foto': '📷',
                'subir_audio': '🎤',
                'subir_video': '🎥',
                'link_wix': '🔗',
                'link_formulario_interno': '📋',
                'link_youtube': '▶️'
              };
              return icons[tipo] || '📋';
            };

            // Para párrafos de lectura, mostrar diferente
            if (pregunta.tipo === 'parrafo_lectura') {
              return (
                <Card key={pregunta.id} className="shadow-md border-l-4 border-l-green-500 bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardHeader className="pb-3">
                    <div className="flex items-start space-x-3">
                      <div className="text-3xl">{getIconByType(pregunta.tipo)}</div>
                      <div className="flex-1">
                        <Badge className="mb-2 bg-green-600 text-white">
                          📖 Lectura - No requiere respuesta
                        </Badge>
                        <div 
                          className="prose prose-sm max-w-none text-gray-800 leading-relaxed prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2"
                          dangerouslySetInnerHTML={{ __html: pregunta.titulo }} 
                        />
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            }

            // Para link de YouTube informativo (solo ver video, no requiere respuesta)
            if (pregunta.tipo === 'link_youtube' && pregunta.video_url) {
              return (
                <Card key={pregunta.id} className="shadow-md border-l-4 border-l-red-500 bg-gradient-to-r from-red-50 to-pink-50">
                  <CardHeader className="pb-3">
                    <div className="flex items-start space-x-3">
                      <div className="text-3xl">{getIconByType(pregunta.tipo)}</div>
                      <div className="flex-1">
                        <Badge className="mb-2 bg-red-600 text-white">
                          ▶️ Video - No requiere respuesta
                        </Badge>
                        <div 
                          className="prose prose-sm max-w-none text-gray-800 leading-relaxed prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 mb-3"
                          dangerouslySetInnerHTML={{ __html: pregunta.titulo }} 
                        />
                        {getTipoInput(pregunta)}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            }

            // Para link de Wix informativo (solo ver formulario embebido, no requiere respuesta directa)
            if (pregunta.tipo === 'link_wix' && pregunta.wix_url) {
              return (
                <Card key={pregunta.id} className="shadow-md border-l-4 border-l-yellow-500 bg-gradient-to-r from-yellow-50 to-amber-50">
                  <CardHeader className="pb-3">
                    <div className="flex items-start space-x-3">
                      <div className="text-3xl">{getIconByType(pregunta.tipo)}</div>
                      <div className="flex-1">
                        <Badge className="mb-2 bg-yellow-600 text-white">
                          🔗 Formulario Externo - No requiere respuesta aquí
                        </Badge>
                        <div 
                          className="prose prose-sm max-w-none text-gray-800 leading-relaxed prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 mb-3"
                          dangerouslySetInnerHTML={{ __html: pregunta.titulo }} 
                        />
                        {getTipoInput(pregunta)}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            }

            // Para link de Formulario Interno de IBA (embebido, no requiere respuesta directa)
            if (pregunta.tipo === 'link_formulario_interno' && pregunta.formulario_publico_url) {
              return (
                <Card key={pregunta.id} className="shadow-md border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardHeader className="pb-3">
                    <div className="flex items-start space-x-3">
                      <div className="text-3xl">{getIconByType(pregunta.tipo)}</div>
                      <div className="flex-1">
                        <Badge className="mb-2 bg-blue-600 text-white">
                          📋 Formulario IBA - No requiere respuesta aquí
                        </Badge>
                        <div 
                          className="prose prose-sm max-w-none text-gray-800 leading-relaxed prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 mb-3"
                          dangerouslySetInnerHTML={{ __html: pregunta.titulo }} 
                        />
                        {getTipoInput(pregunta)}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            }

            // Para preguntas normales
            return (
              <Card key={pregunta.id} className="shadow-md hover:shadow-lg transition-shadow duration-200">
                <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="text-2xl">{getIconByType(pregunta.tipo)}</div>
                      <CardTitle className="text-lg text-gray-900 flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <span className="inline-block bg-blue-600 text-white rounded-full w-8 h-8 text-center leading-8 mr-3 font-bold">
                              {index + 1 - preguntas.slice(0, index).filter(p => 
                                p.tipo === 'parrafo_lectura' || 
                                (p.tipo === 'link_youtube' && p.video_url) || 
                                (p.tipo === 'link_wix' && p.wix_url) ||
                                (p.tipo === 'link_formulario_interno' && p.formulario_publico_url)
                              ).length}
                            </span>
                            <div 
                              className="inline prose prose-sm max-w-none prose-p:my-1 prose-headings:my-1 prose-ul:my-1 prose-ol:my-1"
                              dangerouslySetInnerHTML={{ __html: pregunta.titulo }} 
                            />
                          </div>
                          <Badge variant="secondary" className="ml-4 bg-amber-100 text-amber-800 font-semibold">
                            <i className="fas fa-star mr-1"></i>
                            {pregunta.puntuacion} pts
                          </Badge>
                        </div>
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-4">
                  {getTipoInput(pregunta)}
                </CardContent>
              </Card>
            );
          })}

          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <Button
              type="submit"
              className="instituto-gradient text-white px-8 py-3"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-2"></i>
                  Enviar Formulario
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ResponderFormulario;